// HTTP client: fetch wrapper + auth header injection + 401 single-flight refresh + 30s circuit breaker.
//
// Mirrors xapi-new src/services/apiClient.ts behavior exactly so backend contracts are honored:
//   - Authorization: Bearer ${access} on every authed request
//   - 401 → POST /auth/refresh once, retry; queue concurrent 401s onto the same refresh promise
//   - Refresh failure → mark blockedAt for 30s, clear tokens, fire 'xapi:auth-cleared' event
//   - AbortError preserved (callers can distinguish user-cancelled requests)
//
// All API modules use:  fetchJson('/keys', { method: 'POST', auth: 'always', body: {...} })

import { config } from './config.js';
import { tokens } from './tokens.js';
import {
  clearAuthRefreshBlock,
  isAuthRefreshBlocked,
  refreshAccessTokenShared,
} from '../auth-refresh.js';

export const clearAuthBlock = clearAuthRefreshBlock;
export const isAuthBlocked = isAuthRefreshBlocked;

/**
 * Refresh access token. Single-flight: concurrent callers share the same in-flight promise.
 * Throws on failure; caller decides how to react.
 */
export const refreshAccessToken = () =>
  refreshAccessTokenShared({ apiBase: config.apiBase, tokenStore: tokens });

/**
 * Generic fetch wrapper. Supports:
 *   opts.auth = 'auto' (default — attach token if present)
 *               | 'always' (require token, throw if missing)
 *               | 'none' (no auth header)
 *   opts.body   — object will be JSON.stringify'd; string passed through
 *   opts.headers — merged with Content-Type defaults
 *   opts.signal — AbortSignal for cancellation
 *   opts.raw    — return the raw Response object instead of parsed JSON/text
 *
 * Returns: { ok, status, data, headers }
 * Throws: Error with .status and .data on non-2xx; preserves AbortError.
 */
export async function fetchJson(path, opts = {}) {
  const url = path.startsWith('http') ? path : config.apiBase + path;
  const multipart = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  // The browser must generate the multipart boundary, including on auth retries.
  const headers = { ...(multipart ? {} : { 'Content-Type': 'application/json' }), ...(opts.headers || {}) };
  if (multipart) {
    for (const name of Object.keys(headers)) {
      if (name.toLowerCase() === 'content-type') delete headers[name];
    }
  }
  const auth = opts.auth || 'auto';

  if (auth !== 'none') {
    let t = tokens.getAccess();
    if (!t && auth === 'always' && !path.includes('/auth/refresh')) {
      try { t = await refreshAccessToken(); } catch { /* normalized below */ }
    }
    if (t) headers.Authorization = `Bearer ${t}`;
    else if (auth === 'always') {
      const err = new Error('not authenticated');
      err.status = 401;
      throw err;
    }
  }

  const init = {
    method: opts.method || 'GET',
    credentials: 'include',
    headers,
    signal: opts.signal,
  };
  if (opts.body !== undefined && opts.body !== null) {
    init.body = multipart || typeof opts.body === 'string' ? opts.body : JSON.stringify(opts.body);
  }

  let res;
  try {
    res = await fetch(url, init);
  } catch (e) {
    if (e && (e.name === 'AbortError' || e.code === 20)) throw e;
    const wrapped = new Error('network: ' + (e && e.message ? e.message : e));
    wrapped.cause = e;
    throw wrapped;
  }

  // Single-flight refresh on 401 (skip the refresh endpoint itself to avoid loops).
  if (res.status === 401 && auth !== 'none' && !opts._retried && !path.includes('/auth/refresh')) {
    let refreshed = false;
    try { await refreshAccessToken(); refreshed = true; } catch { /* fall through to parse 401 */ }
    if (refreshed) return fetchJson(path, { ...opts, _retried: true });
  }

  let data = null;
  const ct = res.headers.get('content-type') || '';
  if (opts.raw) {
    data = res;
  } else if (ct.includes('application/json')) {
    try { data = await res.json(); } catch { data = null; }
  } else {
    try { data = await res.text(); } catch { data = null; }
  }

  if (!res.ok) {
    const msg = (data && (data.message || data.error)) || res.statusText || ('HTTP ' + res.status);
    const err = new Error(Array.isArray(msg) ? msg.join('; ') : String(msg));
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return { ok: true, status: res.status, data, headers: res.headers };
}
